import type { PickerUser } from '~/pages/backoffice/admin/sessions/components/ParticipantPicker'
import { useMutation, useQuery } from '@apollo/client/react'
import { Loader } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { GQL_CREATE_GROUP, GQL_GROUP, GQL_UPDATE_GROUP } from '~/gql/groups/groups'
import ParticipantPicker from '~/pages/backoffice/admin/sessions/components/ParticipantPicker'

export type EditableGroup = {
  id: string
  name: string
  description?: string | null
}

type IProps = {
  open: boolean
  onCancel: () => void
  onSuccess: () => void | Promise<unknown>
  users: PickerUser[]
  /** Groupe à éditer ; absent = création. */
  group?: EditableGroup
}

function GroupModal({ open, onCancel, onSuccess, users, group }: IProps) {
  const { t } = useTranslation()
  const isEdit = !!group

  const [name, setName] = useState(group?.name ?? '')
  const [description, setDescription] = useState(group?.description ?? '')
  const [selected, setSelected] = useState<Set<string>>(() => new Set())

  // La composition n'est pas dans la liste (qui n'en porte que le compteur) : on
  // la charge à l'ouverture. Le picker reste masqué tant qu'elle n'est pas
  // arrivée, sinon la réponse réseau écraserait une sélection déjà retouchée.
  const { data: groupData, loading: loadingMembers } = useQuery(GQL_GROUP, {
    variables: { id: group?.id ?? '' },
    skip: !group,
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (groupData?.group) setSelected(new Set(groupData.group.members.map(m => m.id)))
  }, [groupData])

  const [createGroup, { loading: creating }] = useMutation(GQL_CREATE_GROUP)
  const [updateGroup, { loading: updating }] = useMutation(GQL_UPDATE_GROUP)
  const loading = creating || updating

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error(t('groups.nameRequired'))
      return
    }
    const input = {
      name: name.trim(),
      description: description.trim(),
      userIds: [...selected],
    }
    try {
      if (isEdit) {
        await updateGroup({ variables: { id: group.id, input } })
        toast.success(t('groups.updated'))
      } else {
        await createGroup({ variables: { input } })
        toast.success(t('groups.created'))
      }
      await onSuccess()
      onCancel()
    } catch (error) {
      const message = error instanceof Error ? error.message : ''
      toast.error(message.includes('GROUP_NAME_ALREADY_USED')
        ? t('groups.nameUsed')
        : t(isEdit ? 'groups.updateFailed' : 'groups.createFailed'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('groups.update.title') : t('groups.create.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto px-1">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="group-name">{t('common.name')}</label>
            <Input
              id="group-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t('groups.namePlaceholder')}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium" htmlFor="group-description">
              {t('common.description')}
            </label>
            <Input
              id="group-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>

          {loadingMembers
            ? <div className="flex justify-center p-6"><Loader className="size-6 animate-spin" /></div>
            : (
                // Pas de raccourci `groups` ici : on est précisément en train de
                // composer un groupe, s'inscrire soi-même n'aurait pas de sens.
                <ParticipantPicker
                  users={users}
                  value={selected}
                  onChange={setSelected}
                  labelKey="groups.members"
                />
              )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} disabled={loading || loadingMembers}>
            {loading && <Loader className="mr-2 size-4 animate-spin" />}
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default GroupModal
